"use client";

import { IconDownload, IconX, IconXLogo } from "@decibeltrade/react-icons";
import { Button, IconButton } from "@decibeltrade/web-core/components";
import { ModalContent, ModalRenderProps, openModal } from "@decibeltrade/web-core/modal";
import { domToPng } from "modern-screenshot";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { useCallback, useRef } from "react";
import { css } from "styled-system/css";
import { flex, stack } from "styled-system/patterns";
import { token } from "styled-system/tokens";

import { DecibelLogoWordmark } from "@/components/vectors/decibel-logo-wordmark";
import { Logger } from "@/utils/logger";
import { shareToX } from "@/utils/share";

type TierLevel = "gold" | "doublePlatinum" | "diamond" | "top20";

interface ShareCardModalProps {
  points: string;
  rank: string;
  tier: TierLevel;
  tierName: string;
}

const TIER_IMAGES: Record<TierLevel, string> = {
  gold: "/images/points/tier-gold-record.webp",
  doublePlatinum: "/images/points/tier-platinum-record.webp",
  diamond: "/images/points/tier-diamond-record.webp",
  top20: "/images/points/tier-icon-top20.webp",
};

const TIER_SHADOW: Record<TierLevel, string> = {
  gold: "0px 0px 20px 0px rgba(255, 208, 0, 0.5)",
  doublePlatinum: "0px 0px 20px 0px rgba(200, 200, 200, 0.5)",
  diamond: "0px 0px 20px 0px rgba(100, 200, 255, 0.5)",
  top20: "0px 0px 20px 0px rgba(255, 100, 100, 0.5)",
};

export function openShareCardModal(props: ShareCardModalProps) {
  openModal({
    id: "share-card-modal",
    renderContent: (modalProps) => <ShareCardModal {...modalProps} {...props} />,
  });
}

function ShareCardModal({
  points,
  rank,
  tier,
  tierName,
  close,
}: ShareCardModalProps & ModalRenderProps) {
  const t = useTranslations("points.shareCardModal");
  const shareCardRef = useRef<HTMLDivElement | null>(null);

  const onSaveImage = useCallback(async () => {
    if (!shareCardRef.current) return;
    try {
      const dataUrl = await domToPng(shareCardRef.current, {
        scale: 2,
        backgroundColor: token("colors.black"),
        fetch: {
          bypassingCache: true,
        },
      });
      const link = document.createElement("a");
      link.download = `decibel-${tier}-tier.png`;
      link.href = dataUrl;
      link.click();
    } catch (error) {
      Logger.error(error, {
        message: "Failed to generate image for share card",
        tier,
      });
    }
  }, [tier]);

  const handleShareToX = () => {
    const tweetText = t("tweetTemplate", {
      points,
      rank,
      tier: tierName,
    });
    shareToX(tweetText);
  };

  return (
    <ModalContent
      className={css({
        position: "relative",
        w: { base: "[calc(100vw-32px)]", md: "[761px]" },
        maxW: { base: "[calc(100vw-32px)]", md: "[761px]" },
        rounded: "300",
        border: "[1px solid token(colors.border.primary)]",
        bgGradient: "modalBackground",
        overflow: "hidden",
        p: "0",
      })}
    >
      {/* Header */}
      <div
        className={flex({
          justify: "space-between",
          alignItems: "center",
          px: { base: "12", md: "40" },
          h: { base: "[52px]", md: "[84px]" },
          bg: "background.canvas",
          borderBottom: "[1px solid #474747]",
        })}
      >
        <span
          className={css({
            textStyle: { base: "heading.3xs", md: "heading.xs" },
            color: "text.secondary",
          })}
        >
          {t("title")}
        </span>
        <IconButton
          ariaLabel={t("close")}
          variant="text"
          onClick={close}
          className={css({ color: "text.primary" })}
        >
          <IconX />
        </IconButton>
      </div>

      {/* Share Card Content */}
      <div
        ref={shareCardRef}
        className={css({
          position: "relative",
          px: { base: "12", md: "40" },
          py: { base: "16", md: "40" },
          minH: { base: "[260px]", md: "[500px]" },
          overflow: "hidden",
        })}
      >
        {/* Card Header */}
        <div
          className={flex({
            justify: "space-between",
            alignItems: "center",
            mb: { base: "4", md: "8" },
          })}
        >
          <span
            className={css({
              textStyle: { base: "highlight.md", md: "subheading.xs" },
              color: "text.primary",
            })}
          >
            {t("decibelPoints")}
          </span>
          <DecibelLogoWordmark
            className={css({ h: { base: "[16px]", md: "[24px]" }, w: "auto", color: "white" })}
          />
        </div>

        {/* Points */}
        <div className={stack({ gap: "0", mb: { base: "8", md: "24" } })}>
          <span
            className={css({
              textStyle: { base: "highlight.sm", md: "heading.xs" },
              color: "text.tertiary",
              textTransform: "uppercase",
            })}
          >
            {t("points")}
          </span>
          <span
            className={css({
              fontSize: { base: "[32px]", sm: "[48px]", md: "[96px]" },
              lineHeight: { base: "[40px]", sm: "[56px]", md: "[112px]" },
              fontWeight: "light",
              color: "text.secondary",
              maxW: { base: "[calc(100%-100px)]", sm: "[calc(100%-130px)]", md: "auto" },
              overflow: "hidden",
              textOverflow: "ellipsis",
            })}
          >
            {points}
          </span>
        </div>

        {/* Rank */}
        <div className={stack({ gap: "2", mb: { base: "8", md: "16" } })}>
          <span
            className={css({
              textStyle: { base: "highlight.sm", md: "heading.xs" },
              color: "text.tertiary",
              textTransform: "uppercase",
            })}
          >
            {t("rank")}
          </span>
          <span
            className={css({
              fontSize: { base: "[20px]", md: "[32px]" },
              lineHeight: { base: "[28px]", md: "[40px]" },
              fontWeight: "light",
              color: "text.secondary",
            })}
          >
            {rank}
          </span>
        </div>

        {/* Tier */}
        <div className={stack({ gap: "2", mb: { base: "8", md: "16" } })}>
          <span
            className={css({
              textStyle: { base: "highlight.sm", md: "heading.xs" },
              color: "text.tertiary",
              textTransform: "uppercase",
            })}
          >
            {t("tier")}
          </span>
          <span
            className={css({
              fontSize: { base: "[20px]", md: "[32px]" },
              lineHeight: { base: "[28px]", md: "[40px]" },
              fontWeight: "regular",
              color: "text.secondary",
            })}
          >
            {tierName}
          </span>
        </div>

        {/* Record Image */}
        <div
          className={css({
            position: "absolute",
            right: { base: "[8px]", sm: "[16px]", md: "[60px]" },
            top: { base: "[50px]", sm: "[80px]", md: "[120px]" },
            w: { base: "[120px]", sm: "[140px]", md: "[300px]" },
            h: { base: "[120px]", sm: "[140px]", md: "[300px]" },
            rounded: "full",
          })}
          style={{ boxShadow: TIER_SHADOW[tier] }}
        >
          <Image
            src={TIER_IMAGES[tier]}
            alt={tierName}
            width={300}
            height={300}
            className={css({ w: "full", h: "full", objectFit: "cover" })}
            unoptimized
          />
        </div>
      </div>

      {/* Footer */}
      <div
        className={flex({
          gap: "8",
          px: { base: "12", md: "40" },
          py: { base: "12", md: "0" },
          h: { base: "auto", md: "[84px]" },
          bg: "background.canvas",
          borderTop: "[1px solid #474747]",
          flexDirection: { base: "column", sm: "row" },
          justifyContent: { base: "center", md: "flex-end" },
          alignItems: "center",
        })}
      >
        <Button
          variant="secondary"
          className={css({
            rounded: "full",
            px: { base: "12", sm: "16", md: "32" },
            py: { base: "[6px]", sm: "8", md: "12" },
            h: { base: "[36px]", sm: "40", md: "48" },
            textStyle: { base: "highlight.lg", md: "highlight.md" },
            letterSpacing: { base: "[1px]", md: "[1.56px]" },
            textTransform: "uppercase",
            w: { base: "full", md: "auto" },
          })}
          LeadIcon={IconDownload}
          onClick={onSaveImage}
        >
          {t("saveImage")}
        </Button>

        <Button
          variant="secondary"
          className={css({
            rounded: "full",
            px: { base: "12", sm: "16", md: "32" },
            py: { base: "[6px]", sm: "8", md: "12" },
            h: { base: "[36px]", sm: "40", md: "48" },
            textStyle: { base: "highlight.lg", md: "highlight.md" },
            letterSpacing: { base: "[1px]", md: "[1.56px]" },
            textTransform: "uppercase",
            w: { base: "full", md: "auto" },
          })}
          LeadIcon={IconXLogo}
          onClick={handleShareToX}
        >
          {t("shareOnX")}
        </Button>
      </div>
    </ModalContent>
  );
}
